import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Button,
  Modal,
  message,
  Descriptions,
  Image,
  Steps,
  Select,
  Spin,
  Input,
  Row,
  Col,
  Divider,
  Tabs,Form,
  Popconfirm,
  Tooltip,
  Alert
} from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  SyncOutlined,
  FilterOutlined,
  DownloadOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { getUserData } from "../../../hooks/Auth";
const { Item } = Form;
const { Title, Text } = Typography;
const { Step } = Steps;
const { Option } = Select;
const { TabPane } = Tabs;
  const user = getUserData();

const API_BASE_URL = "https://techcrew.pk/ulc/";

const PaymentVerification = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [verificationStatus, setVerificationStatus] = useState("Pending");
  const [verificationNotes, setVerificationNotes] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [form] = Form.useForm();

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_PaymentsForVerification.php`);
      
      if (data.status.code === 200) {
        setPayments(data.data);
        setFilteredPayments(data.data);
      } else {
        throw new Error(data.status.message);
      }
    } catch (error) {
      message.error(error.message || "Failed to fetch payments");
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_Users.php`);
      if (data.status.code === 200) {
        const supplierList = data.data.filter(user => user.role === 'supplier');
        setSuppliers(supplierList);
      }
    } catch (error) {
      console.error("Failed to fetch suppliers", error);
    }
  };

  const verifyPayment = async (values) => {
    setVerifying(true);
    try {
      const payload = {
        payment_id: selectedPayment.payment_id,
        status: verificationStatus,
        notes: verificationNotes,
        verified_by: user?.user?.id || 0,
        supplier_id: selectedSupplier || null
      };
      console.log("Verifying payment with payload:", payload,selectedSupplier);

      const { data } = await axios.post(`${API_BASE_URL}Verify_Payment.php`, payload);
      
      if (data.status.code === 200) {
        message.success(`Payment ${verificationStatus.toLowerCase()} successfully`);
        fetchPayments();
        setModalVisible(false);
        resetVerificationForm();
      } else {
        throw new Error(data.status.message);
      }
    } catch (error) {
      message.error(error.response?.data?.status?.message || error.message || "Failed to update payment status");
    } finally {
      setVerifying(false);
    }
  };

  const resetVerificationForm = () => {
    setVerificationStatus("Pending");
    setVerificationNotes("");
    setSelectedSupplier(null);
    setCurrentStep(0);
    form.resetFields();
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setModalVisible(true);
    resetVerificationForm();
  };
  const handleTabChange = (key) => {
    setActiveTab(key);
    filterPayments(key, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    filterPayments(activeTab, value);
  };
useEffect(() => {
    fetchPayments();
    fetchSuppliers();
  }, []);
  const filterPayments = (statusFilter, searchValue) => {
    let filtered = [...payments];
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(payment => 
        statusFilter === "pending" 
          ? payment.status !== "Verified" && payment.status !== "Rejected"
          : payment.status === statusFilter
      );
    }
    
    // Apply search filter
    if (searchValue) {
      const lowerSearch = searchValue.toLowerCase();
      filtered = filtered.filter(payment => 
        payment.payment_id.toString().includes(lowerSearch) ||
        payment.order_id.toString().includes(lowerSearch) ||
        payment.transaction_id?.toLowerCase().includes(lowerSearch) ||
        payment.user_id.toString().includes(lowerSearch)
      );
    }
    
    setFilteredPayments(filtered);
  };

  const downloadReceipt = () => {
    if (selectedPayment?.receipt_path) {
      const link = document.createElement('a');
      link.href = `${API_BASE_URL}uploads/payment/${selectedPayment.receipt_path}`;
      link.download = `receipt_${selectedPayment.payment_id}.${selectedPayment.receipt_path.split('.').pop()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      message.warning("No receipt available to download");
    }
  };

  const columns = [
    {
      title: 'Payment ID',
      dataIndex: 'payment_id',
      key: 'payment_id',
      render: (id) => <Text strong>#{id}</Text>,
      sorter: (a, b) => a.payment_id - b.payment_id,
    },
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      render: (id) => <Text>#{id}</Text>
    },
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary">{record.email}</Text>
        </div>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong>
          {parseFloat(amount).toLocaleString("en-US", {
            style: "currency",
            currency: "SAR",
          })}
        </Text>
      ),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Method',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method) => (
        <Tag color={method === 'Bank Transfer' ? 'blue' : method === 'Credit Card' ? 'purple' : 'cyan'}>
          {method}
        </Tag>
      )
    },
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => moment(date).format('DD MMM YYYY HH:mm'),
      sorter: (a, b) => new Date(a.payment_date) - new Date(b.payment_date),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color, icon;
        switch (status) {
          case 'Verified':
            color = 'green';
            icon = <CheckOutlined />;
            break;
          case 'Rejected':
            color = 'red';
            icon = <CloseOutlined />;
            break;
          default:
            color = 'orange';
            icon = <SyncOutlined spin />;
        }
        return (
          <Tag icon={icon} color={color} style={{ fontWeight: 500 }}>
            {status}
          </Tag>
        );
      },
      filters: [
        { text: 'Pending', value: 'Pending' },
        { text: 'Verified', value: 'Verified' },
        { text: 'Rejected', value: 'Rejected' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="View details">
            <Button 
              icon={<EyeOutlined />}
              onClick={() => handleViewPayment(record)}
              shape="circle"
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchPayments();
  }, []);

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={<Title level={4} style={{ margin: 0 }}>Payment Verification Dashboard</Title>}
        bordered={false}
        extra={
          <Space>
            <Input.Search 
              placeholder="Search payments..." 
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
            <Button 
              icon={<SyncOutlined />} 
              onClick={fetchPayments}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        }
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarExtraContent={
            <Text type="secondary" style={{ marginRight: 8 }}>
              Showing {filteredPayments.length} of {payments.length} payments
            </Text>
          }
        >
          <TabPane tab="All Payments" key="all" />
          <TabPane tab="Pending Verification" key="pending" />
          <TabPane tab="Verified" key="Verified" />
          <TabPane tab="Rejected" key="Rejected" />
        </Tabs>
        
        <Table
          columns={columns}
          dataSource={filteredPayments}
          rowKey="payment_id"
          loading={loading}
          scroll={{ x: true }}
          bordered
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} payments`,
          }}
          rowClassName={(record) => 
            record.status === 'Rejected' ? 'rejected-row' : 
            record.status === 'Verified' ? 'verified-row' : ''
          }
        />
      </Card>

      <Modal
        title={
          <Space>
            <span>Payment Verification - #{selectedPayment?.payment_id}</span>
            {selectedPayment && (
              <Tag color={
                selectedPayment.status === 'Verified' ? 'green' :
                selectedPayment.status === 'Rejected' ? 'red' : 'orange'
              }>
                {selectedPayment.status}
              </Tag>
            )}
          </Space>
        }
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          resetVerificationForm();
        }}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          <Step title="Details" />
          <Step title="Receipt" />
          <Step title="Decision" />
        </Steps>

        {currentStep === 0 && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Order ID" span={2}>
                <Text strong>#{selectedPayment?.order_id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="User">
                <div>
                  <Text strong>{selectedPayment?.name}</Text>
                  <br />
                  <Text type="secondary">{selectedPayment?.email}</Text>
                  <br />
                  <Text>{selectedPayment?.phone}</Text>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Date">
                {moment(selectedPayment?.payment_date).format('DD MMM YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Amount" span={2}>
                <Text strong style={{ fontSize: 18 }}>
                  {parseFloat(selectedPayment?.amount).toLocaleString("en-US", {
                    style: "currency",
                    currency: "SAR",
                  })}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                <Tag color="blue">{selectedPayment?.payment_method}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Transaction ID">
                {selectedPayment?.transaction_id || 'N/A'}
              </Descriptions.Item>
              {selectedPayment?.notes && (
                <Descriptions.Item label="Notes" span={2}>
                  <Text type="secondary">{selectedPayment.notes}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Button 
                type="primary" 
                onClick={() => setCurrentStep(1)}
              >
                Next: Review Receipt
              </Button>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <div>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
              <Col>
                <Title level={5} style={{ margin: 0 }}>Payment Receipt</Title>
              </Col>
              <Col>
                <Button 
                  icon={<DownloadOutlined />}
                  onClick={downloadReceipt}
                  disabled={!selectedPayment?.receipt_path}
                >
                  Download
                </Button>
              </Col>
            </Row>
            
            {selectedPayment?.receipt_path ? (
              <div style={{ textAlign: 'center', border: '1px dashed #d9d9d9', padding: 16 }}>
                <Image
                  width="100%"
                  style={{ maxHeight: 400, objectFit: 'contain' }}
                  src={`${API_BASE_URL}uploads/payment/${selectedPayment.receipt_path}`}
                  alt="Payment Receipt"
                  fallback="https://via.placeholder.com/300x400?text=Receipt+Not+Available"
                  preview={{
                    mask: <span>Click to zoom</span>
                  }}
                />
              </div>
            ) : (
              <Alert
                message="No Receipt Uploaded"
                description="This payment doesn't have a receipt attached."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Button 
                style={{ marginRight: 8 }} 
                onClick={() => setCurrentStep(0)}
              >
                Back
              </Button>
              <Button 
                type="primary" 
                onClick={() => setCurrentStep(2)}
                disabled={!selectedPayment?.receipt_path}
              >
                {selectedPayment?.receipt_path ? 'Continue to Verification' : 'Skip to Verification'}
              </Button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <Title level={5} style={{ marginBottom: 16 }}>Verification Decision</Title>
            
            <Alert
              message="Important"
              description="Please carefully review the payment details and receipt before making a decision."
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            
            <div style={{ marginBottom: 24 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Verification Status <Text type="secondary">(Required)</Text>
              </Text>
              <Select
                style={{ width: '100%' }}
                value={verificationStatus}
                onChange={setVerificationStatus}
              >
                <Option value="Verified">
                  <Tag color="green">Verified</Tag> - Payment is confirmed
                </Option>
                <Option value="Rejected">
                  <Tag color="red">Rejected</Tag> - Payment is invalid
                </Option>
              </Select>
            </div>
{verificationStatus === 'Verified' && (
              <Item
                name="supplier_id"
                label={<Text strong>Supplier <Text type="secondary">(If applicable)</Text></Text>}
                tooltip="Select supplier if this payment is for supplier equipment"
              >
                <Select
                  style={{ width: '100%' }}
                  placeholder="Select supplier (if applicable)"
                  allowClear
                  onChange={(value) => setSelectedSupplier(value)}
                >
                  {suppliers.map(supplier => (
                    <Option key={supplier.user_id} value={supplier.user_id}
                    
                    >
                      {supplier.name} ({supplier.email})
                    </Option>
                  ))}
                </Select>
              </Item>
            )}

            <div style={{ marginBottom: 24 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Verification Notes <Text type="secondary">(Optional)</Text>
              </Text>
              <Input.TextArea 
                rows={4} 
                placeholder="Add any notes about this verification..." 
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
              />
            </div>

            <Divider />

            <div style={{ textAlign: 'center' }}>
              <Space>
                <Popconfirm
                  title="Are you sure you want to reject this payment?"
                  onConfirm={() => verifyPayment('Rejected')}
                  okText="Yes, Reject"
                  cancelText="Cancel"
                  placement="top"
                  disabled={verificationStatus !== 'Rejected'}
                >
                  <Button 
                    type="primary" 
                    danger
                    icon={<CloseOutlined />}
                    loading={verifying}
                    disabled={verificationStatus !== 'Rejected'}
                  >
                    Reject Payment
                  </Button>
                </Popconfirm>
                
                <Popconfirm
                  title="Are you sure you want to verify this payment?"
                  onConfirm={() => verifyPayment('Verified')}
                  okText="Yes, Verify"
                  cancelText="Cancel"
                  placement="top"
                  disabled={verificationStatus !== 'Verified'}
                >
                  <Button 
                    type="primary" 
                    icon={<CheckOutlined />}
                    loading={verifying}
                    disabled={verificationStatus !== 'Verified'}
                  >
                    Verify Payment
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          </div>
        )}
      </Modal>

      <style jsx global>{`
        .ant-table-row.verified-row {
          background-color: #f6ffed;
        }
        .ant-table-row.rejected-row {
          background-color: #fff1f0;
        }
        .ant-steps-item-process .ant-steps-item-icon {
          background: #1890ff;
        }
        .ant-steps-item-process .ant-steps-item-icon .ant-steps-icon {
          color: white;
        }
      `}</style>
    </div>
  );
};

export default PaymentVerification;